import { useMemo } from 'react'
import { Alert, Box, Card, CardContent, CircularProgress, Grid, Typography } from '@mui/material'
import { CanonicalPeriod, EmissionTier } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { HeatStressScoreBadge } from '../../common/heatStress/utils'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import { getSelectedPeriodPointY, getSucceededMetricData, hasAnyCanonicalPeriodData } from '../overviewMetrics'

const formatNumber = (value: number | null, digits = 1) => {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

type HeatStressOverviewProps = {
  propertyId: number
  emissionTier: EmissionTier
  selectedPeriod: CanonicalPeriod
}

function HeatStressOverview({ propertyId, emissionTier, selectedPeriod }: HeatStressOverviewProps) {
  const {
    data: heatScores,
    isLoading: heatScoresLoading,
    error: heatScoresError,
  } = usePropertyClimateData('hs:heat_scores', { propertyId })
  const {
    data: hotDays,
    isLoading: hotDaysLoading,
    error: hotDaysError,
  } = usePropertyClimateData('hs:hot_days', { propertyId })

  const heatScoresPayload = getSucceededMetricData(heatScores)
  const hotDaysPayload = getSucceededMetricData(hotDays)
  const selectedHeatScore = getSelectedPeriodPointY(heatScoresPayload, emissionTier, selectedPeriod)
  const selectedHotDays = getSelectedPeriodPointY(hotDaysPayload, emissionTier, selectedPeriod)
  const overviewHasData = useMemo(
    () =>
      hasAnyCanonicalPeriodData([
        period => getSelectedPeriodPointY(heatScoresPayload, emissionTier, period),
        period => getSelectedPeriodPointY(hotDaysPayload, emissionTier, period),
      ]),
    [emissionTier, heatScoresPayload, hotDaysPayload],
  )

  const overviewLoading = ((heatScoresLoading && !heatScores) || (hotDaysLoading && !hotDays)) && !overviewHasData
  const overviewErrored = !overviewHasData && (heatScoresError != null || hotDaysError != null)

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box mb={2}>
          <ClimateSectionTitle
            title="Overview"
            subtitle="Heat stress conditions for the selected period"
            helpText="Overview metrics use the horizon selected in the page header."
            loading={heatScoresLoading || hotDaysLoading}
          />
        </Box>

        {overviewLoading && (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={140}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!overviewLoading && overviewHasData && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title="Heat Stress Rating"
                variant="outlined"
                helpText="Heat stress rating for the selected horizon and emissions scenario."
                loading={heatScoresLoading}
              >
                {selectedHeatScore != null ? (
                  <HeatStressScoreBadge score={selectedHeatScore} size="compact" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Heat stress rating is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ClimateMetricCard
                title="Hot Days"
                variant="outlined"
                helpText="Projected count of local hot days per year for the selected horizon."
                loading={hotDaysLoading}
              >
                {selectedHotDays != null ? (
                  <Typography variant="h5">{formatNumber(selectedHotDays, 0)} days/year</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Hot days are unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>
        )}

        {!overviewLoading && !overviewHasData && (
          <Alert severity={overviewErrored ? 'error' : 'info'}>
            {overviewErrored ? 'Unable to load heat stress overview data.' : 'No overview data available.'}
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default HeatStressOverview
